import { useCallback, useEffect, useState } from 'react';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';

interface Member {
  id: string;
  role: Role;
  name: string;
  email: string | null;
  phone: string | null;
  partnerId: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

type Role = 'manager' | 'sales_rep' | 'pre_sales' | 'consultant' | 'partner' | 'contractor';

const ROLES: { value: Role; label: string }[] = [
  { value: 'manager',    label: 'Gerente de ventas' },
  { value: 'sales_rep',  label: 'Representante de ventas' },
  { value: 'pre_sales',  label: 'Pre-ventas' },
  { value: 'consultant', label: 'Consultor en entrega' },
  { value: 'partner',    label: 'Partner' },
  { value: 'contractor', label: 'Contratista' },
];

const ROLE_LABEL: Record<Role, string> = Object.fromEntries(
  ROLES.map((r) => [r.value, r.label]),
) as Record<Role, string>;

const ROLE_ORDER: Role[] = ['manager', 'sales_rep', 'pre_sales', 'consultant', 'partner', 'contractor'];

const EMPTY_FORM = {
  role: 'sales_rep' as Role,
  name: '',
  email: '',
  phone: '',
  partnerId: '',
  notes: '',
};

export function SalesTeamPage() {
  const [members, setMembers] = useState<Member[]>([]);
  const [editing, setEditing] = useState<Member | 'new' | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const { data } = await apiClient.get<Member[]>('/sales-team');
    setMembers(data);
  }, []);

  useEffect(() => { load().catch(() => setMembers([])); }, [load]);

  function startNew() {
    setForm(EMPTY_FORM);
    setEditing('new');
    setError(null);
  }

  function startEdit(m: Member) {
    setForm({
      role: m.role,
      name: m.name,
      email: m.email ?? '',
      phone: m.phone ?? '',
      partnerId: m.partnerId ?? '',
      notes: m.notes ?? '',
    });
    setEditing(m);
    setError(null);
  }

  function cancel() {
    setEditing(null);
    setError(null);
  }

  async function save() {
    if (!form.name.trim()) { setError('El nombre es obligatorio.'); return; }
    setSaving(true);
    setError(null);
    const body = {
      role: form.role,
      name: form.name.trim(),
      email: form.email.trim() || null,
      phone: form.phone.trim() || null,
      partnerId: form.partnerId || null,
      notes: form.notes.trim() || null,
    };
    try {
      if (editing === 'new') {
        await apiClient.post('/sales-team', body);
      } else if (editing) {
        await apiClient.patch(`/sales-team/${editing.id}`, body);
      }
      await load();
      setEditing(null);
    } catch {
      setError('No se pudo guardar. Intenta de nuevo.');
    } finally {
      setSaving(false);
    }
  }

  async function remove(id: string) {
    if (!window.confirm('¿Eliminar este miembro del equipo?')) return;
    try {
      await apiClient.delete(`/sales-team/${id}`);
      await load();
    } catch {
      // non-fatal
    }
  }

  // Partners for the contractor partner_id dropdown
  const partners = members.filter((m) => m.role === 'partner');

  const grouped = ROLE_ORDER.map((role) => ({
    role,
    items: members.filter((m) => m.role === role),
  })).filter((g) => g.items.length > 0 || editing === 'new');

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Equipo de ventas</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Gerentes, representantes, pre-ventas, consultores, partners y contratistas.
          </p>
        </div>
        <Button onClick={startNew} disabled={editing !== null}>
          + Agregar miembro
        </Button>
      </div>

      {/* Create / edit form */}
      {editing !== null && (
        <div className="rounded-lg border bg-card p-6">
          <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {editing === 'new' ? 'Nuevo miembro' : 'Editar miembro'}
          </h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Rol *</label>
              <Select
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value as Role })}
              >
                {ROLES.map((r) => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </Select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Nombre *</label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Nombre completo"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Email</label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="correo@ejemplo.com"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Teléfono</label>
              <Input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="+1 555 000 0000"
              />
            </div>
            {form.role === 'contractor' && (
              <div>
                <label className="mb-1 block text-xs font-medium text-muted-foreground">
                  Partner asociado
                </label>
                <Select
                  value={form.partnerId}
                  onChange={(e) => setForm({ ...form, partnerId: e.target.value })}
                >
                  <option value="">— Contratista independiente —</option>
                  {partners.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </Select>
              </div>
            )}
            <div className="sm:col-span-2">
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Notas</label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={2}
                placeholder="Observaciones opcionales…"
              />
            </div>
          </div>
          {error && <p className="mt-3 text-xs text-destructive">{error}</p>}
          <div className="mt-4 flex gap-2">
            <Button onClick={save} disabled={saving}>
              {saving ? 'Guardando…' : 'Guardar'}
            </Button>
            <Button variant="ghost" onClick={cancel} disabled={saving}>
              Cancelar
            </Button>
          </div>
        </div>
      )}

      {/* Team list grouped by role */}
      {members.length === 0 && editing === null ? (
        <p className="py-12 text-center text-sm text-muted-foreground">
          Aún no hay miembros del equipo. Agrega el primero con el botón de arriba.
        </p>
      ) : (
        <div className="space-y-4">
          {grouped.map(({ role, items }) => (
            <div key={role} className="overflow-hidden rounded-lg border">
              <div className="flex items-center gap-2 border-b bg-muted/40 px-4 py-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {ROLE_LABEL[role]}
                </span>
                <span className="ml-auto rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                  {items.length}
                </span>
              </div>
              {items.length === 0 ? (
                <p className="px-4 py-3 text-sm text-muted-foreground">Sin miembros en esta categoría.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="border-b bg-muted/20 text-xs text-muted-foreground">
                    <tr>
                      <th className="px-4 py-2 text-left font-medium">Nombre</th>
                      <th className="px-4 py-2 text-left font-medium">Email</th>
                      <th className="px-4 py-2 text-left font-medium">Teléfono</th>
                      {role === 'contractor' && (
                        <th className="px-4 py-2 text-left font-medium">Partner</th>
                      )}
                      <th className="w-24 px-4 py-2 text-right font-medium">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {items.map((m) => (
                      <tr key={m.id} className="hover:bg-muted/20">
                        <td className="px-4 py-2.5 font-medium">{m.name}</td>
                        <td className="px-4 py-2.5 text-muted-foreground">
                          {m.email ? (
                            <a href={`mailto:${m.email}`} className="hover:text-foreground hover:underline">
                              {m.email}
                            </a>
                          ) : '—'}
                        </td>
                        <td className="px-4 py-2.5 text-muted-foreground">{m.phone ?? '—'}</td>
                        {role === 'contractor' && (
                          <td className="px-4 py-2.5 text-muted-foreground">
                            {m.partnerId
                              ? (members.find((p) => p.id === m.partnerId)?.name ?? '—')
                              : 'Independiente'}
                          </td>
                        )}
                        <td className="px-4 py-2.5 text-right">
                          <div className="flex justify-end gap-1">
                            <button
                              onClick={() => startEdit(m)}
                              disabled={editing !== null}
                              className="rounded px-2 py-1 text-xs text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-40"
                            >
                              Editar
                            </button>
                            <button
                              onClick={() => remove(m.id)}
                              disabled={editing !== null}
                              className="rounded px-2 py-1 text-xs text-destructive hover:bg-destructive/10 disabled:opacity-40"
                            >
                              Eliminar
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
